/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {useIntl} from 'react-intl'
import {formatPhoneNumber} from '@salesforce/retail-react-app/app/utils/phone-utils'

export default function useCheckoutLoginFields({
    form: {
        control,
        formState: {errors}
    },
    prefix = ''
}) {
    const {formatMessage} = useIntl()
    const unformatPhoneNumber = (value) => value.replace(/\D/g, '')
    const fields = {
        email: {
            name: `${prefix}email`,
            label: formatMessage({
                defaultMessage: 'Start by Entering Your Email',
                id: 'use_login_fields.label.email_field'
            }),
            placeholder: 'example@email.com',
            defaultValue: '',
            type: 'email',
            autoComplete: 'email',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your email address.',
                    id: 'use_login_fields.error.required_email'
                }),
                pattern: {
                    value: /^\S+@\S+\.\S+$/, // simple email regex
                    message: formatMessage({
                        defaultMessage: 'Please enter valid email address.',
                        id: 'use_login_fields.error.valid_email'
                    })
                }
            },
            error: errors?.[`${prefix}email`],
            control,
            inputProps: ({onChange}) => ({
                onChange(e) {
                    const email = e.target.value
                    onChange(email)
                    if (email) {
                        localStorage.setItem('passwordResetEmail', email)
                    }
                }
            })
        },
        password: {
            name: `${prefix}password`,
            label: formatMessage({
                defaultMessage: 'Enter your password',
                id: 'use_login_fields.label.password_field'
            }),
            defaultValue: '',
            type: 'password',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your password.',
                    id: 'use_login_fields.error.required_password'
                })
            },
            error: errors?.[`${prefix}password`],
            control
        },
        mobile: {
            name: `${prefix}mobile`,
            label: formatMessage({
                defaultMessage: 'Enter Your Phone Number',
                id: 'use_registration_fields.label.checkout_mobile'
            }),
            type: 'tel',
            autoComplete: 'tel',
            defaultValue: '',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter a valid mobile phone.',
                    id: 'use_registration_fields.error.required_mobile'
                }),
                pattern: {
                    value: /^1?[2-9][0-9]{2}[2-9][0-9]{6}$/,
                    message: formatMessage({
                        defaultMessage: 'Please enter a valid mobile phone.',
                        id: 'use_registration_fields.error.invalid_us_mobile'
                    })
                }
            },
            inputProps: ({value, onChange}) => ({
                inputMode: 'numeric',
                value: formatPhoneNumber(value),
                onChange(e) {
                    onChange(unformatPhoneNumber(e.target.value))
                }
            }),
            error: errors[`${prefix}mobile`],
            control
        }
    }
    return fields
}
